import type { ChannelNameForm, Route } from './routes.ts';

/**
 * The identity under which a scroll position belongs to "the same screen".
 *
 * Two hrefs with one identity are the same mounted page wearing different controls — a search
 * filter, a channel sort — and navigating between them must keep the reader's place. Two hrefs
 * with different identities are a screen switch, and a switch starts at the top. The web router
 * consumes this for its scroll behaviour; it lives here because Android's back stack needs the
 * identical table, and because the per-arm choices below are route-shaped decisions, not styling.
 *
 * The choices that are not mechanical:
 *
 * - `watch` is the video alone: `t`, `list` and `index` are riders on the same page.
 * - `search` is the question plus the page — a pager click reads from the top — while the five
 *   filters stay out, which is the same line `App.tsx` draws by keying the screen on `q`.
 * - `channel` is the channel plus the tab (a tab swaps the whole body) but not the sort; the
 *   in-channel search is its own identity per question and page, like `/results`.
 * - The resolving arms and `not-found` key on the path itself — every distinct lookup is its own
 *   screen, because its outcome is.
 */
export function screenIdentity(route: Route): string {
  switch (route.kind) {
    case 'home':
      return 'home';
    case 'watch':
      return `watch:${route.videoId}`;
    case 'embed':
      // The video alone, the watch arm's own reading: `start` is a rider on the same page — and a
      // video is the same video whichever list it is being played from, so `list` is a rider too.
      // A list embed that has not resolved yet has no video to be identified by, so it is identified
      // by the entry it is about to become.
      return route.videoId === null
        ? `embed:list:${route.list?.playlistId ?? ''}:${String(route.list?.index ?? 0)}`
        : `embed:${route.videoId}`;
    case 'playlist':
      return `playlist:${route.playlistId}`;
    case 'feed':
      return `feed:${route.feed}`;
    case 'channel':
      return `channel:${route.channelId}:${route.tab}`;
    case 'channelSearch':
      return `channel:${route.channelId}:search:${route.q}:${route.page}`;
    case 'channelName':
      return `resolve:${channelNamePrefixOf(route.form)}${route.name}:${route.tab}`;
    case 'clip':
      return `resolve:clip:${route.clipId}`;
    case 'post':
      return `post:${route.postId}`;
    case 'hashtag':
      return `hashtag:${route.tag}:${route.page}`;
    case 'search':
      return `search:${route.search.q}:${route.search.page}`;
    case 'login':
      return 'login';
    case 'settings':
      return `settings:${route.tab}`;
    case 'not-found':
      return `not-found:${route.path}`;
  }
}

function channelNamePrefixOf(form: ChannelNameForm): string {
  return form === 'handle' ? '@' : `${form}/`;
}
